import React from 'react'
import { Flex, Text } from '@grammarly/design-system'

import { OutputPopoverExplanation } from '@components/dynamic/ai-detector/output-components'
import { detectedAIColor, noDetectedAIcolor, smallFoldHeight } from '@components/tool/constants'
import { ProgressIndicator } from './progress-indicator'
import { ToolLoading } from '../tool-loading'
import { ToolOutputContentProps } from '../../types'
import { useMediaQuery } from '@lib/hooks'

export const AIDetector: React.FC<ToolOutputContentProps> = ({ isLoading, outputContent }) => {
  if (isLoading) {
    return <ToolLoading />
  }

  if (outputContent) {
    return outputContent
  }

  return <AIDetectorOutput aiPercent={null} />
}

interface AIDetectorOutputProps {
  aiPercent: number | null
}

const AIDetectorOutputTitle: React.FC<{ aiPercent: number | null }> = ({ aiPercent }) => {
  const title =
    aiPercent === null
      ? '—% of this text appears to be AI-generated'
      : `${aiPercent}% of this text appears to be AI-generated`
  return (
    <Text variant="heading-medium" as="p" color="base-default" align="center">
      {title}
    </Text>
  )
}

export const AIDetectorOutput: React.FC<AIDetectorOutputProps> = ({ aiPercent }) => {
  const isSmallerFoldHeight = useMediaQuery(`(max-height: ${smallFoldHeight}px)`)

  return (
    <Flex
      direction="column"
      justify="center"
      align="center"
      width="100%"
      height="100%"
      gap={isSmallerFoldHeight ? 6 : 8}
    >
      <Flex direction="row" align="center">
        <AIDetectorOutputTitle aiPercent={aiPercent} />
        <OutputPopoverExplanation />
      </Flex>
      <ProgressIndicator percentage={aiPercent} />
      <Flex direction="column" align="center" gap={2} style={{ width: '70%' }}>
        <AIDetectorScore color={detectedAIColor} copy="Resembles AI text" score={aiPercent} />
        <AIDetectorScore
          color={noDetectedAIcolor}
          copy="No AI text patterns found"
          score={aiPercent === null ? null : 100 - aiPercent}
        />
      </Flex>
    </Flex>
  )
}

interface AIDetectorScoreProps {
  color: string
  copy: string
  score: number | null
}

const AIDetectorScore: React.FC<AIDetectorScoreProps> = ({ color, copy, score }) => {
  return (
    <Flex direction="row" align="center" width="100%" gap={2}>
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="8" fill={color} />
      </svg>
      <Text variant="text-medium" as="span" color="base-default">
        {copy}
      </Text>
      <Text variant="text-medium" as="span" marginLeft="auto" color="base-default">
        {score !== null ? `${score}%` : '—%'}
      </Text>
    </Flex>
  )
}
